import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Download, Plus, RefreshCw } from 'lucide-react';
import { type JSX, useState } from 'react';
import { PageHeader } from '~/components/data';
import { Button, Input, Select } from '~/components/primitives';
import { createFxOverride, fetchFxRates, triggerNbkzFetch, triggerRevaluation } from '~/lib/api/fx';
import styles from './_auth.admin.fx-rates.module.css';

/**
 * D.9 — Admin FX rates page. Read list + manual override form + trigger NBKZ fetch + trigger revaluation.
 */

export const Route = createFileRoute('/_auth/admin/fx-rates')({ component: FxRatesAdmin });

function FxRatesAdmin(): JSX.Element {
  const qc = useQueryClient();
  const [currencyFilter, setCurrencyFilter] = useState<string>('');
  const query = useQuery({
    queryKey: ['admin', 'fx-rates', currencyFilter],
    queryFn: () => fetchFxRates(currencyFilter ? { currency: currencyFilter } : {}),
  });
  const rows = query.data?.items ?? [];

  const [overrideOpen, setOverrideOpen] = useState(false);
  const [currency, setCurrency] = useState<'USD' | 'EUR' | 'RUB' | 'CNY'>('USD');
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [rate, setRate] = useState('');
  const [note, setNote] = useState('');

  const createMut = useMutation({
    mutationFn: () =>
      createFxOverride({
        base_currency: currency,
        quote_currency: 'KZT',
        as_of_date: asOf,
        rate,
        note: note || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'fx-rates'] });
      setOverrideOpen(false);
      setRate('');
      setNote('');
    },
  });

  const nbkzMut = useMutation({
    mutationFn: () => triggerNbkzFetch(),
    onSuccess: (res) => {
      alert(`NBKZ: inserted ${res.inserted} rows for ${res.as_of}`);
      qc.invalidateQueries({ queryKey: ['admin', 'fx-rates'] });
    },
  });

  const revalMut = useMutation({
    mutationFn: () => triggerRevaluation(),
    onSuccess: (res) => {
      alert(`Revaluation: ${res.inserted} adjustments on ${res.as_of}`);
    },
  });

  return (
    <div className={styles.page}>
      <PageHeader title={m.route_admin_fx_rates_title()} />

      <div className={styles.toolbar}>
        <Select
          label={m.route_admin_fx_rates_filter_currency()}
          value={currencyFilter}
          onChange={(e) => setCurrencyFilter(e.target.value)}
          options={[
            { value: '', label: '—' },
            { value: 'USD', label: 'USD' },
            { value: 'EUR', label: 'EUR' },
            { value: 'RUB', label: 'RUB' },
            { value: 'CNY', label: 'CNY' },
          ]}
        />
        <div className={styles.spacer} />
        <Button variant="ghost" onClick={() => nbkzMut.mutate()} loading={nbkzMut.isPending}>
          <Download size={16} /> {m.route_admin_fx_rates_nbkz_fetch()}
        </Button>
        <Button variant="ghost" onClick={() => revalMut.mutate()} loading={revalMut.isPending}>
          <RefreshCw size={16} /> {m.route_admin_fx_rates_reval()}
        </Button>
        <Button variant="primary" onClick={() => setOverrideOpen(!overrideOpen)}>
          <Plus size={16} /> {m.route_admin_fx_rates_new_override()}
        </Button>
      </div>

      {overrideOpen && (
        <div className={styles.overrideForm}>
          <Select
            label={m.route_admin_fx_rates_col_currency()}
            value={currency}
            onChange={(e) => setCurrency(e.target.value as 'USD' | 'EUR' | 'RUB' | 'CNY')}
            options={[
              { value: 'USD', label: 'USD' },
              { value: 'EUR', label: 'EUR' },
              { value: 'RUB', label: 'RUB' },
              { value: 'CNY', label: 'CNY' },
            ]}
          />
          <Input label="Date" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          <Input
            label={m.route_admin_fx_rates_col_rate()}
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder="450.00"
          />
          <Input label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
          <Button
            variant="primary"
            onClick={() => createMut.mutate()}
            loading={createMut.isPending}
          >
            {m.common_button_save()}
          </Button>
        </div>
      )}

      <table className={styles.table}>
        <thead>
          <tr className={styles.theadRow}>
            <th className={styles.cell}>{m.route_admin_fx_rates_col_currency()}</th>
            <th className={styles.cell}>{m.route_admin_fx_rates_col_date()}</th>
            <th className={styles.cell}>{m.route_admin_fx_rates_col_rate()}</th>
            <th className={styles.cell}>{m.route_admin_fx_rates_col_source()}</th>
            <th className={styles.cell}>{m.route_admin_fx_rates_col_note()}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={`${r.base_currency}-${r.as_of_date}-${r.source}-${i}`}
              className={styles.tbodyRow}
            >
              <td className={styles.cellMono}>
                {r.base_currency}/{r.quote_currency}
              </td>
              <td className={styles.cell}>{r.as_of_date}</td>
              <td className={styles.cellTabular}>{r.rate}</td>
              <td className={styles.cellSubtle}>{r.source}</td>
              <td className={styles.cell}>{r.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
